/*
  One Tooth World — <otw-search> (PRD §6.2 "Search bar", §9).
  Rotating placeholder, predictive search (debounced 250ms, stale requests aborted),
  keyboard navigation, recent searches (localStorage, last 5), popular searches and
  the mobile full-screen overlay. Shares state with other modules through window.OTW only.
*/
const OTW = (window.OTW = window.OTW || {});
const RECENT_KEY = 'otw_recent_searches';
const RECENT_MAX = 5;
const mobileQuery = window.matchMedia('(max-width: 749px)');
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const str = (key, fallback) => (OTW.strings && OTW.strings[key]) || fallback;

/* ---------- Recent searches store ---------- */
function readRecent() {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(list) ? list.filter((t) => typeof t === 'string').slice(0, RECENT_MAX) : [];
  } catch (e) {
    return [];
  }
}

function writeRecent(list) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
  } catch (e) {
    /* storage blocked */
  }
  document.querySelectorAll('otw-search').forEach((el) => el.renderRecent?.());
}

function saveRecent(term) {
  const value = String(term || '').trim().slice(0, 80);
  if (!value) return;
  const list = readRecent().filter((t) => t.toLowerCase() !== value.toLowerCase());
  list.unshift(value);
  writeRecent(list);
}

const searchUrl = () => (window.routes && window.routes.search_url) || `${window.Shopify?.routes?.root || '/'}search`;
const suggestUrl = () => (window.routes && window.routes.predictive_search_url) || '/search/suggest';

let uid = 0;

class OtwSearch extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.uid = `s${++uid}`;
    this.mode = this.dataset.mode || 'inline';
    this.form = this.querySelector('.otw-search__form');
    this.input = this.querySelector('.otw-search__input');
    this.panel = this.querySelector('.otw-search__panel');
    this.results = this.querySelector('[data-results]');
    this.idle = this.querySelector('[data-idle]');
    this.status = this.querySelector('[data-status]');
    this.recentWrap = this.querySelector('[data-recent-wrap]');
    this.recentList = this.querySelector('[data-recent]');
    this.clearButton = this.querySelector('.otw-search__clear');
    this.camera = this.querySelector('.otw-search__camera');
    this.predictive = this.dataset.predictive === 'true';
    this.cache = new Map();
    this.activeIndex = -1;
    if (!this.input || !this.panel) return;

    this.fetchDebounced = debounce(() => this.fetchResults(), 250);

    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('focus', (event) => this.onFocus(event));
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));
    this.form.addEventListener('submit', (event) => this.onSubmit(event));
    this.clearButton?.addEventListener('click', () => {
      this.input.value = '';
      this.onInput();
      this.input.focus();
    });
    this.camera?.addEventListener('click', () => {
      if (this.wantsOverlay()) this.openOverlay(this.camera);
      else this.input.focus();
    });
    this.panel.addEventListener('click', (event) => {
      if (event.target.closest('[data-clear-recent]')) {
        writeRecent([]);
        this.input.focus();
        return;
      }
      const option = event.target.closest('[role="option"]');
      if (option) saveRecent(option.dataset.term || this.input.value);
    });
    this.panel.addEventListener('mousemove', (event) => {
      const option = event.target.closest('[role="option"]');
      if (option) this.setActive(this.options().indexOf(option), false);
    });

    if (this.mode === 'inline') {
      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.closePanel();
      };
      document.addEventListener('click', this.onDocumentClick);
      this.addEventListener('focusout', () => {
        setTimeout(() => {
          if (!this.contains(document.activeElement)) this.closePanel();
        });
      });
    }

    this.renderRecent();
    this.syncValueState();
    this.setupPlaceholder();
  }

  disconnectedCallback() {
    if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
    clearInterval(this.placeholderTimer);
    this.placeholderObserver?.disconnect();
    this.controller?.abort();
  }

  /* ---------- Overlay (mobile) ---------- */
  wantsOverlay() {
    return Boolean(this.dataset.overlay) && mobileQuery.matches && document.getElementById(this.dataset.overlay);
  }

  openOverlay(opener) {
    const drawer = document.getElementById(this.dataset.overlay || 'OtwSearchOverlay');
    if (!drawer || typeof drawer.open !== 'function') return false;
    drawer.open(opener || this.input);
    const overlaySearch = drawer.querySelector('otw-search');
    if (overlaySearch && overlaySearch.input) {
      // Focus synchronously inside the user gesture so iOS shows the keyboard
      overlaySearch.input.focus({ preventScroll: true });
      overlaySearch.openPanel();
    }
    return true;
  }

  /* ---------- Events ---------- */
  onFocus(event) {
    if (this.wantsOverlay()) {
      // Focus coming back from the overlay (drawer focus restore) must not reopen it
      const overlay = document.getElementById(this.dataset.overlay);
      if (event.relatedTarget && overlay.contains(event.relatedTarget)) return;
      this.input.blur();
      this.openOverlay(this.input);
      return;
    }
    this.openPanel();
  }

  onInput() {
    this.syncValueState();
    const query = this.input.value.trim();
    this.openPanel();
    if (!query) {
      this.controller?.abort();
      this.showIdle();
      return;
    }
    if (this.predictive) this.fetchDebounced();
  }

  onSubmit(event) {
    const query = this.input.value.trim();
    if (!query) {
      event.preventDefault();
      this.input.focus();
      return;
    }
    saveRecent(query);
  }

  onKeydown(event) {
    const options = this.options();
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.openPanel();
        if (options.length) this.setActive(this.activeIndex + 1 >= options.length ? 0 : this.activeIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (options.length) this.setActive(this.activeIndex <= 0 ? options.length - 1 : this.activeIndex - 1);
        break;
      case 'Enter': {
        const active = options[this.activeIndex];
        if (active && this.isPanelOpen()) {
          event.preventDefault();
          saveRecent(active.dataset.term || this.input.value);
          active.click();
        }
        break;
      }
      case 'Escape':
        if (this.mode === 'inline' && this.isPanelOpen()) {
          event.preventDefault();
          event.stopPropagation();
          this.closePanel();
        }
        // Overlay mode: the drawer handles Esc and closes the overlay
        break;
      default:
        break;
    }
  }

  /* ---------- Panel ---------- */
  isPanelOpen() {
    return this.mode === 'overlay' || !this.panel.hidden;
  }

  openPanel() {
    if (this.mode === 'inline') {
      if (!this.panel.hidden) return;
      this.panel.hidden = false;
      this.classList.add('is-open');
    }
    this.input.setAttribute('aria-expanded', 'true');
    if (!this.input.value.trim()) this.showIdle();
    else if (this.results.hidden && this.results.innerHTML) this.showResults();
  }

  closePanel() {
    if (this.mode !== 'inline' || this.panel.hidden) return;
    this.panel.hidden = true;
    this.classList.remove('is-open');
    this.input.setAttribute('aria-expanded', 'false');
    this.setActive(-1);
  }

  showIdle() {
    this.results.hidden = true;
    this.idle.hidden = false;
    this.setActive(-1);
    this.classList.toggle('is-empty', !this.options().length);
  }

  showResults() {
    this.idle.hidden = true;
    this.results.hidden = false;
    this.classList.remove('is-empty');
    this.setActive(-1);
  }

  options() {
    const scope = this.results.hidden ? this.idle : this.results;
    return Array.from(scope.querySelectorAll('[role="option"]'));
  }

  setActive(index, scroll = true) {
    const options = this.options();
    options.forEach((option, i) => {
      const active = i === index;
      option.classList.toggle('is-active', active);
      option.setAttribute('aria-selected', String(active));
    });
    this.activeIndex = index >= 0 && index < options.length ? index : -1;
    const active = options[this.activeIndex];
    if (active) {
      this.input.setAttribute('aria-activedescendant', active.id);
      if (scroll) active.scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  syncValueState() {
    const hasValue = this.input.value.length > 0;
    this.classList.toggle('has-value', hasValue);
    if (this.clearButton) this.clearButton.hidden = !hasValue;
  }

  /* ---------- Predictive search ---------- */
  async fetchResults() {
    const query = this.input.value.trim();
    if (!query) return;
    const key = query.toLowerCase();
    if (this.cache.has(key)) {
      this.renderResults(this.cache.get(key), query);
      return;
    }

    this.controller?.abort();
    this.controller = new AbortController();
    this.classList.add('is-loading');
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product,collection,query',
      'resources[limit]': '6',
      'resources[options][unavailable_products]': 'last',
      section_id: this.dataset.section || 'otw-predictive-search',
    });

    try {
      const response = await fetch(`${suggestUrl()}?${params.toString()}`, {
        signal: this.controller.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const section = doc.querySelector('.otw-ps');
      const html = section ? section.outerHTML : '';
      this.cache.set(key, html);
      if (this.input.value.trim() === query) this.renderResults(html, query);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.renderResults('', query);
    } finally {
      this.classList.remove('is-loading');
    }
  }

  renderResults(html, query) {
    if (this.input.value.trim() !== query) return;
    this.results.innerHTML = html;
    // Unique option ids per search bar
    this.results.querySelectorAll('[id]').forEach((el) => {
      el.id = `${this.uid}-${el.id}`;
    });
    this.results.querySelectorAll('[aria-labelledby]').forEach((el) => {
      el.setAttribute('aria-labelledby', `${this.uid}-${el.getAttribute('aria-labelledby')}`);
    });
    if (!html) {
      this.showIdle();
      return;
    }
    this.showResults();
    const count = Number(this.results.querySelector('.otw-ps')?.dataset.count || 0);
    if (this.status) {
      this.status.textContent = str('searchResultsCount', '[count] results available.').replace('[count]', count);
    }
  }

  /* ---------- Recent searches ---------- */
  renderRecent() {
    if (!this.recentList) return;
    const list = readRecent();
    this.recentList.textContent = '';
    list.forEach((term, i) => {
      const li = document.createElement('li');
      const link = document.createElement('a');
      link.className = 'otw-chip otw-search__chip otw-search__chip--recent';
      link.setAttribute('role', 'option');
      link.setAttribute('aria-selected', 'false');
      link.id = `${this.uid}-recent-${i}`;
      link.dataset.term = term;
      const url = new URL(searchUrl(), window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('options[prefix]', 'last');
      link.href = url.pathname + url.search;
      link.textContent = term;
      li.appendChild(link);
      this.recentList.appendChild(li);
    });
    if (this.recentWrap) this.recentWrap.hidden = list.length === 0;
  }

  /* ---------- Rotating placeholder ---------- */
  setupPlaceholder() {
    this.terms = Array.from(this.querySelectorAll('.otw-search__ph-term'));
    if (this.terms.length < 2) return;
    this.termIndex = 0;
    this.interval = Number(this.dataset.interval) || 3000;
    this.visible = true;

    if ('IntersectionObserver' in window) {
      this.placeholderObserver = new IntersectionObserver((entries) => {
        this.visible = entries.some((entry) => entry.isIntersecting);
      });
      this.placeholderObserver.observe(this.form);
    }

    this.placeholderTimer = setInterval(() => {
      if (document.hidden || !this.visible) return;
      if (this.classList.contains('has-value') || document.activeElement === this.input) return;
      this.rotatePlaceholder();
    }, this.interval);
  }

  rotatePlaceholder() {
    const current = this.terms[this.termIndex];
    this.termIndex = (this.termIndex + 1) % this.terms.length;
    const next = this.terms[this.termIndex];
    current.classList.remove('is-active');
    if (!reducedMotion()) {
      current.classList.add('is-leaving');
      setTimeout(() => current.classList.remove('is-leaving'), 350);
    }
    next.classList.remove('is-leaving');
    next.classList.add('is-active');
  }
}

function debounce(fn, wait) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

if (!customElements.get('otw-search')) customElements.define('otw-search', OtwSearch);

/* ---------- Global API: OTW.search.open() / OTW.emit('search:open') ---------- */
OTW.search = {
  open(opener) {
    const header = document.querySelector('otw-search[data-overlay]');
    if (mobileQuery.matches) {
      const overlayId = header?.dataset.overlay || 'OtwSearchOverlay';
      const drawer = document.getElementById(overlayId);
      if (drawer && typeof drawer.open === 'function') {
        drawer.open(opener);
        const overlaySearch = drawer.querySelector('otw-search');
        overlaySearch?.input?.focus({ preventScroll: true });
        overlaySearch?.openPanel?.();
        return true;
      }
    }
    const target = header || document.querySelector('otw-search');
    if (target && target.input) {
      target.input.focus();
      return true;
    }
    return false;
  },
  saveRecent,
  readRecent,
};

if (OTW.on && !OTW.searchListening) {
  OTW.searchListening = true;
  OTW.on('search:open', (detail = {}) => OTW.search.open(detail.opener));
}

// Close the overlay if the viewport grows past mobile while it is open
mobileQuery.addEventListener('change', (event) => {
  if (event.matches) return;
  document.querySelectorAll('otw-drawer.otw-search-overlay.is-open').forEach((drawer) => drawer.close({ restoreFocus: false }));
});
